import { GetStaticProps, InferGetStaticPropsType } from 'next'
import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote'
// eslint-disable-next-line import/no-internal-modules
import { serialize } from 'next-mdx-remote/serialize'

import { AuthorData } from '@/components/author-card'
import { GradientBlock } from '@/components/blocks/gradient-block'
import { BlueImage } from '@/components/blue-image'
import { Footer, FooterProps } from '@/components/footer/footer'
import { Header } from '@/components/header/header'
import { TransLink } from '@/components/trans-link'
import { getAllSlugs } from '@/helper/get-all-slugs'
import { getBooksByAuthorSlug } from '@/helper/get-books-by-author-slug'
import { getDataBySlug } from '@/helper/get-data-by-slug'
import { getImageSize } from '@/helper/get-image-size'
import { getMenuData } from '@/helper/get-menu-data'

export interface AuthorProps {
  data: AuthorData
  source: MDXRemoteSerializeResult<Record<string, unknown>>
  menuData: FooterProps['data']
}

export default function AuthorSingleView(
  props: InferGetStaticPropsType<typeof getStaticProps>
) {
  if (!props || props == {}) return null
  const { data, source, menuData } = props as AuthorProps
  const { fullName, imageSrc, books, width, height } = data

  return (
    <>
      <Header small />
      <h1>{fullName}</h1>
      <section className="serif">
        <div className="content">
          <MDXRemote
            {...source}
            components={{ GradientBlock: GradientBlock }}
          />
          Bei uns erschienen:
          <ul className="links">
            {books
              ? books.map((book) => (
                  <li key={book.slug}>
                    <TransLink href={'/books/' + book.slug}>
                      {book.title}
                    </TransLink>
                  </li>
                ))
              : null}
          </ul>
        </div>
        {imageSrc ? (
          <BlueImage
            imageSrc={imageSrc}
            alt={`Buchcover ${fullName}`}
            maxWidth="50"
            width={width}
            height={height}
          />
        ) : (
          <div className="spacer">&nbsp;</div>
        )}
      </section>
      {/* <GradientBlock height="30vh" /> */}
      <style jsx>{`
        section {
          @media (min-width: 780px) {
            display: flex;
            justify-content: space-between;
            .content {
              max-width: 47%;
            }
            .spacer,
            .img-wrapper {
              width: 47%;
              max-width: 47%;
              margin-top: 5rem;
            }
          }
        }
        .img-wrapper {
          background-color: var(--brand);
          img {
            width: 100%;
            filter: grayscale(100%);
            mix-blend-mode: luminosity;
            opacity: 0.7;
          }
        }
      `}</style>
      <Footer data={menuData} />
    </>
  )
}

export const getStaticProps: GetStaticProps<AuthorProps | {}> = async ({
  params,
}) => {
  const slug = params?.slug?.toString()
  if (!slug) return { props: {} }

  const { data } = getDataBySlug('authors', slug)
  const books = getBooksByAuthorSlug(slug)
  const mdxSource = await serialize(data.description)
  const sizes = getImageSize(data)

  const menuData = getMenuData()

  return {
    props: { data: { ...data, books, ...sizes }, source: mdxSource, menuData },
  }
}

export async function getStaticPaths() {
  const slugs = getAllSlugs('authors')

  return {
    paths: slugs.map((slug) => {
      return {
        params: {
          slug,
        },
      }
    }),
    fallback: false,
  }
}
